import { QuickActions } from "./QuickActions";
import { Bot, Hand, MessageCircle, Send } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  useDevelopmentFixtures,
  whatsappApi,
  type CustomerCareSettings,
  type WhatsAppConversation,
  type WhatsAppMessage,
} from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { WhatsAppMessages } from "../../components/WhatsAppMessages";
import { Button, StatePanel, StatusBadge } from "../../components/ui";
import { useLiveSync } from "../../hooks/useLiveSync";


/** The customer's WhatsApp conversation inside the case: read, answer and take over without leaving. */
export function CaseChat({
  conversationId: linkedConversationId,
  customerName,
  phone,
  empty,
  showName = false,
  initialReply = "",
}: {
  conversationId: string | null;
  customerName: string;
  phone?: string | undefined;
  empty?: ReactNode;
  /** Outside a case page the chat header names the customer. */
  showName?: boolean;
  /** A ready message (from a task on "Hoy") left in the box to review and send. */
  initialReply?: string;
}) {
  const { hasPermission } = useAuth();
  // A chat started from here shows at once, before the case reloads with it linked.
  const [startedId, setStartedId] = useState<string | null>(null);
  const conversationId = linkedConversationId ?? startedId;
  const canRead = hasPermission("whatsapp.read");
  const canReply = hasPermission("whatsapp.reply");
  const canHandoff = hasPermission("whatsapp.handoff");
  const [conversation, setConversation] = useState<WhatsAppConversation | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[] | null>(null);
  const [settings, setSettings] = useState<CustomerCareSettings | null>(null);
  const [reply, setReply] = useState(initialReply);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const generation = useRef(0);
  const load = useCallback(async () => {
    if (!conversationId || !canRead) return;
    const current = ++generation.current;
    if (useDevelopmentFixtures) {
      const { demoConversation, demoEscalatedConversation, demoWhatsAppMessages } = await import("../../dev/whatsapp-fixtures");
      if (current !== generation.current) return;
      setConversation((existing) => existing ?? ([demoConversation, demoEscalatedConversation].find((item) => item.id === conversationId) ?? {
        ...demoConversation, id: conversationId, phone_e164: phone ?? null, customer_name: customerName,
      }));
      setMessages((existing) => existing ?? demoWhatsAppMessages(conversationId));
      return;
    }
    try {
      const [detail, status] = await Promise.all([
        whatsappApi.detail(conversationId),
        settings ? Promise.resolve(settings) : whatsappApi.status(),
      ]);
      if (current !== generation.current) return;
      setConversation(detail.data.conversation);
      setMessages(detail.data.messages);
      setSettings(status);
      setError("");
    } catch (caught) {
      if (current === generation.current)
        setError(caught instanceof Error ? caught.message : "No pudimos cargar la conversación.");
    }
  }, [conversationId, canRead, customerName, phone, settings]);
  useEffect(() => {
    setConversation(null);
    setMessages(null);
    setReply("");
    setError("");
  }, [conversationId]);
  useEffect(() => {
    void load();
    // `load` changes with settings; the conversation id is what selects the chat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, canRead]);
  useLiveSync({
    onRefresh: load,
    aggregateTypes: ["whatsapp_conversation"],
    pollIntervalMs: 20000,
  });
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages?.length]);
  const connected = useDevelopmentFixtures || settings?.whatsapp.connection_status === "connected";
  const run = async (work: () => Promise<unknown>, failure: string) => {
    setWorking(true);
    setError("");
    try {
      await work();
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : failure);
    } finally {
      setWorking(false);
    }
  };
  const send = () => {
    const body = reply.trim();
    if (!conversationId || !body) return;
    void run(async () => {
      if (useDevelopmentFixtures)
        setMessages((current) => [...(current ?? []), { id: crypto.randomUUID(), direction: "outbound", sender_type: "user", body, status: "sent", created_at: new Date().toISOString() }]);
      else {
        await whatsappApi.reply(conversationId, body);
        window.setTimeout(() => void load(), 2_500);
      }
      setReply("");
    }, "No pudimos enviar el mensaje.");
  };
  // The customer never wrote: the first message opens the chat with their number.
  const start = () => {
    const body = reply.trim();
    if (!phone || !body) return;
    void run(async () => {
      if (useDevelopmentFixtures) setStartedId(crypto.randomUUID());
      else {
        const result = await whatsappApi.start(phone, body, customerName);
        setStartedId(result.data.conversationId);
      }
      setReply("");
    }, "No pudimos enviar el mensaje.");
  };
  const setAssistant = (enabled: boolean) =>
    void run(async () => {
      if (useDevelopmentFixtures)
        setConversation((current) => current && { ...current, ai_enabled: enabled, status: enabled ? "open" : "waiting_human" });
      else if (enabled) await whatsappApi.delegate(conversationId!);
      else await whatsappApi.handoff(conversationId!);
    }, enabled ? "No pudimos devolver la conversación al asistente." : "No pudimos tomar la conversación.");

  if (!canRead || !conversationId)
    return (
      <section className="case-chat case-chat--empty" aria-label="Conversación de WhatsApp">
        <header>
          <MessageCircle size={18} aria-hidden="true" />
          <strong>WhatsApp</strong>
        </header>
        <div className="case-chat__empty">
          {empty ?? (
            <StatePanel
              state="empty"
              title={phone ? "Aún no hay conversación" : "Sin WhatsApp"}
              description={phone ? `${phone} todavía no nos ha escrito. Puedes enviarle el primer mensaje.` : "Este cliente no tiene teléfono registrado."}
            />
          )}
        </div>
        {canRead && canReply && phone && (
          <footer className="case-chat__starter">
            {error && <p className="case-chat__error" role="alert">{error}</p>}
            <textarea
              aria-label={`Primer mensaje para ${phone}`}
              value={reply}
              maxLength={4000}
              placeholder={`Escribe el primer mensaje para ${phone}… (Ctrl + Enter para enviar)`}
              onChange={(event) => setReply(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                  event.preventDefault();
                  start();
                }
              }}
            />
            <Button icon={Send} disabled={working || !reply.trim()} onClick={start}>
              {working ? "Enviando…" : "Enviar"}
            </Button>
          </footer>
        )}
      </section>
    );
  return (
    <section className="whatsapp-thread case-chat" aria-label="Conversación de WhatsApp">
      <header>
        <span>
          <strong>
            <MessageCircle size={16} aria-hidden="true" /> {showName ? (conversation?.customer_name ?? customerName) : "WhatsApp"}
          </strong>
          <small>{conversation?.phone_e164 ?? phone ?? ""}</small>
        </span>
        {conversation && (
          <div className="whatsapp-thread-control">
            <StatusBadge tone={conversation.ai_enabled ? "info" : "warning"}>
              {conversation.ai_enabled ? "Responde el asistente" : "Atiendes tú"}
            </StatusBadge>
            {canHandoff &&
              (conversation.ai_enabled ? (
                <Button variant="secondary" icon={Hand} disabled={working} onClick={() => setAssistant(false)}>
                  Tomar conversación
                </Button>
              ) : (
                <Button variant="quiet" icon={Bot} disabled={working} onClick={() => setAssistant(true)}>
                  Devolver al asistente
                </Button>
              ))}
          </div>
        )}
      </header>
      {messages === null && !error ? (
        <div className="whatsapp-messages"><StatePanel state="loading" title="Cargando conversación" /></div>
      ) : messages?.length === 0 ? (
        <div className="whatsapp-messages"><p className="workspace-empty">Aún no hay mensajes.</p></div>
      ) : (
        <WhatsAppMessages
          messages={messages ?? []}
          listRef={listRef}
          canRetry={canReply}
          retryDisabled={working || !connected}
          onRetry={(message) => void run(() => whatsappApi.retry(message.outbox_id!), "No pudimos reintentar el mensaje.")}
        />
      )}
      {error && (
        <p role="alert" className="case-chat__error">
          {error}
        </p>
      )}
      <footer>
        <textarea
          aria-label="Escribe tu mensaje"
          value={reply}
          maxLength={4000}
          placeholder={connected ? "Escribe tu mensaje… (Ctrl + Enter para enviar)" : "WhatsApp no está conectado"}
          disabled={!canReply || !connected}
          onChange={(event) => setReply(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
              event.preventDefault();
              send();
            }
          }}
        />
        <div className="case-chat__send">
          <Button icon={Send} disabled={working || !reply.trim() || !canReply || !connected} onClick={send}>
            Enviar
          </Button>
          <QuickActions
            customerName={conversation?.customer_name ?? customerName}
            disabled={working || !canReply || !connected || !conversationId}
            onSendBrochure={async (level, caption) => {
              if (!conversationId) return;
              if (useDevelopmentFixtures)
                setMessages((current) => [...(current ?? []), { id: crypto.randomUUID(), direction: "outbound", sender_type: "user", body: `📄 ${caption}`, status: "sent", created_at: new Date().toISOString() }]);
              else {
                await whatsappApi.sendBrochure(conversationId, level, caption);
                window.setTimeout(() => void load(), 2_500);
              }
            }}
          />
        </div>
      </footer>
    </section>
  );
}
